/**
 * Component Demo Page
 * Showcases all UI components from Phase 3
 */

import { useState } from 'react'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Badge,
  Spinner,
  Input,
  Textarea,
  Select,
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  AppShell,
  Header,
  Sidebar,
  SidebarItem,
  SidebarSection,
  SidebarHeader,
  EditorToolbar,
  WordCount,
  AutoSaveIndicator,
} from '@/components'
import { Book, FileText, Settings, HelpCircle, Home } from 'lucide-react'

export function ComponentDemo() {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [sampleText, setSampleText] = useState(
    'This is a sample text to demonstrate the word count feature. Type here to see real-time statistics!',
  )
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'error' | 'idle'>('saved')
  const [activePage, setActivePage] = useState('home')

  return (
    <AppShell
      header={<Header title="Ebook Genius - Component Demo" />}
      sidebar={
        <Sidebar width={240}>
          <SidebarSection>
            <SidebarHeader>Navigation</SidebarHeader>
            <SidebarItem
              icon={<Home className="h-5 w-5" />}
              label="Home"
              active={activePage === 'home'}
              onClick={() => setActivePage('home')}
            />
            <SidebarItem
              icon={<Book className="h-5 w-5" />}
              label="My Books"
              active={activePage === 'books'}
              onClick={() => setActivePage('books')}
            />
            <SidebarItem
              icon={<FileText className="h-5 w-5" />}
              label="Editor"
              active={activePage === 'editor'}
              onClick={() => setActivePage('editor')}
            />
          </SidebarSection>
          <SidebarSection>
            <SidebarHeader>Other</SidebarHeader>
            <SidebarItem
              icon={<Settings className="h-5 w-5" />}
              label="Settings"
              active={activePage === 'settings'}
              onClick={() => setActivePage('settings')}
            />
            <SidebarItem
              icon={<HelpCircle className="h-5 w-5" />}
              label="Help"
              active={activePage === 'help'}
              onClick={() => setActivePage('help')}
            />
          </SidebarSection>
        </Sidebar>
      }
    >
      <div className="p-8 space-y-8 max-w-6xl mx-auto">
        {/* Page Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">UI Component Library</h1>
          <p className="text-gray-600">
            Showcasing all components from Phase 3 implementation
          </p>
        </div>

        {/* Buttons Section */}
        <Card>
          <CardHeader>
            <CardTitle>Buttons</CardTitle>
            <CardDescription>Different button variants and sizes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="danger">Danger</Button>
                <Button variant="success">Success</Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm">Small</Button>
                <Button size="md">Medium</Button>
                <Button size="lg">Large</Button>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button loading>Loading</Button>
                <Button disabled>Disabled</Button>
                <Button fullWidth>Full Width</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Badges Section */}
        <Card>
          <CardHeader>
            <CardTitle>Badges</CardTitle>
            <CardDescription>Status indicators and labels</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="default">Default</Badge>
              <Badge variant="primary">Primary</Badge>
              <Badge variant="success">Success</Badge>
              <Badge variant="warning">Warning</Badge>
              <Badge variant="danger">Danger</Badge>
              <Badge variant="info">Info</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Forms Section */}
        <Card>
          <CardHeader>
            <CardTitle>Form Components</CardTitle>
            <CardDescription>Input fields with validation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-w-md">
              <Input label="Title" placeholder="Enter book title" required fullWidth />
              <Input
                label="Email"
                type="email"
                placeholder="email@example.com"
                helperText="We'll never share your email"
                fullWidth
              />
              <Input
                label="Invalid Field"
                error="This field has an error"
                defaultValue="Invalid value"
                fullWidth
              />
              <Textarea
                label="Description"
                placeholder="Enter book description..."
                rows={4}
                fullWidth
              />
              <Select label="Genre" fullWidth>
                <option value="">Select a genre</option>
                <option value="fiction">Fiction</option>
                <option value="non-fiction">Non-Fiction</option>
                <option value="sci-fi">Science Fiction</option>
                <option value="mystery">Mystery</option>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Modal Section */}
        <Card>
          <CardHeader>
            <CardTitle>Modal Dialog</CardTitle>
            <CardDescription>Accessible modal with backdrop</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => setIsModalOpen(true)}>Open Modal</Button>
          </CardContent>
        </Card>

        {/* Editor Components Section */}
        <Card>
          <CardHeader>
            <CardTitle>Editor Components</CardTitle>
            <CardDescription>Text editor toolbar and statistics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <EditorToolbar
                onFormat={format => console.log('Format:', format)}
              />

              <Textarea
                value={sampleText}
                onChange={e => setSampleText(e.target.value)}
                rows={6}
                fullWidth
                placeholder="Type here to see word count update..."
              />

              <div className="flex items-center justify-between">
                <WordCount text={sampleText} />
                <AutoSaveIndicator status={saveStatus} lastSaved={new Date()} />
              </div>

              <div className="flex gap-2">
                <Button size="sm" onClick={() => setSaveStatus('saving')}>
                  Simulate Saving
                </Button>
                <Button size="sm" onClick={() => setSaveStatus('saved')}>
                  Simulate Saved
                </Button>
                <Button size="sm" variant="danger" onClick={() => setSaveStatus('error')}>
                  Simulate Error
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Loading States Section */}
        <Card>
          <CardHeader>
            <CardTitle>Loading States</CardTitle>
            <CardDescription>Spinners and loading indicators</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6">
              <div className="flex flex-col items-center gap-2">
                <Spinner size="sm" />
                <span className="text-xs text-gray-500">Small</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Spinner size="md" />
                <span className="text-xs text-gray-500">Medium</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Spinner size="lg" />
                <span className="text-xs text-gray-500">Large</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Spinner size="xl" />
                <span className="text-xs text-gray-500">Extra Large</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cards Section */}
        <Card hover>
          <CardHeader>
            <CardTitle>Card with Hover Effect</CardTitle>
            <CardDescription>This card has a hover shadow effect</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700">
              Cards can be used to group related content. They support headers, bodies, and
              footers. Hover over this card to see the effect!
            </p>
          </CardContent>
          <CardFooter>
            <div className="flex gap-2">
              <Button size="sm">Action</Button>
              <Button size="sm" variant="secondary">
                Cancel
              </Button>
            </div>
          </CardFooter>
        </Card>

        {/* Phase Completion Status */}
        <Card>
          <CardHeader>
            <CardTitle>Phase 3 Status</CardTitle>
            <CardDescription>Component library implementation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Badge variant="success">✓</Badge>
                <span>Basic UI Components (Button, Card, Badge, Spinner)</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="success">✓</Badge>
                <span>Form Components (Input, Textarea, Select)</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="success">✓</Badge>
                <span>Modal Dialog</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="success">✓</Badge>
                <span>Layout Components (Header, Sidebar, AppShell)</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="success">✓</Badge>
                <span>Editor Components (Toolbar, WordCount, AutoSave)</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal */}
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalHeader>
          <ModalTitle>Example Modal</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p className="text-gray-700">
            This is a modal dialog. It can be dismissed by clicking the X button, pressing
            Escape, or clicking outside the modal.
          </p>
          <div className="mt-4">
            <Input
              label="Example Input"
              placeholder="You can add forms in modals"
              fullWidth
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => setIsModalOpen(false)}>Confirm</Button>
        </ModalFooter>
      </Modal>
    </AppShell>
  )
}
